//@ts-check

class CashflowStatementDataService {
	constructor(
		$sce,
		$http,
		Utility,
		$timeout,
		$rootScope,
		ContentService,
		FinancialsService,
		LocalStorageService,
		InternalLinksService
	) {
		this.$sce = $sce;
		this.$http = $http;
		this.Utility = Utility;
		this.$timeout = $timeout;
		this.$rootScope = $rootScope;
		this.ContentService = ContentService;
		this.FinancialsService = FinancialsService;
		this.LocalStorageService = LocalStorageService;
		this.InternalLinksService = InternalLinksService;

		this.init();
	}

	async init() {
		await this.InternalLinksService.getLinks('/api/financial/links');

		this.statements = [];
		this.transactions = {};
		this.yearlyTransactions = {};
		this.cashflowData = undefined;
		this.yearlyCashflowData = undefined;
	}

	async getCashflowStatement(schemeRef, date) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialApiCashflowStatement');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiCashflowStatement');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			date = this.FinancialsService.getMapFirstDay(date);

			const headers = this.Utility.headersWithContentType;

			if (!!!headers || (!!headers && (!!!headers.userName || !!!headers.sessionToken))) {
				const error = `Could not load Cashflow as the 'Username' and/or 'Session Token' is missing`;
				this.$timeout(() => promiseHandler.reject(error));
			}

			const results = await this.$http.post(url, date, { headers });

			const { data } = results;
			
			this.cashflowData = data;
			this.leviesRecievedSummary = this.cashflowData.LeviesRecievedSummary;

			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getYearlyCashflowStatement(schemeRef, date) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialApiYearlyCashflowStatement');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiYearlyCashflowStatement');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			date = this.FinancialsService.getMapFirstDay(date);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, date, { headers });

			const { data } = results;
			this.yearlyCashflowData = data;
			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getTransactions(schemeRef, date, accountCode, isInterest) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialApiTransactions');
			if (isInterest) {
				url = await this.InternalLinksService.getLink('FinancialApiInterestTransactions');
			}

			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				if (isInterest) {
					url = await this.InternalLinksService.getLink('FinancialApiInterestTransactions');
				} else {
					url = await this.InternalLinksService.getLink('FinancialApiTransactions');
				}
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			url = `${url}?accountCode=${accountCode}`;
			date = this.FinancialsService.getMapFirstDay(date);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, date, { headers });
			const { data } = results;

			if (`${accountCode}` in this.transactions) {
				delete this.transactions[accountCode];
			}

			this.transactions[accountCode] = data;

			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async loadStatements(selectedBuilding, selectedDate, accountCode) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialApiRecordAttachment');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiRecordAttachment');
			}

			url = url.replace('{schemeRef}', selectedBuilding).trim();
			url += `?date=${selectedDate}&accountCode=${accountCode}`;

			const headers = this.Utility.headersWithContentType;
			const data = await this.$http.get(url, { headers });

			this.$timeout(() => promiseHandler.resolve(data.data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async loadPolicyDocuments(selectedBuilding) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('GetLatestDocumentAttachmentsForBuildingAndType');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/documents/links');
				url = await this.InternalLinksService.getLink('GetLatestDocumentAttachmentsForBuildingAndType');
			}

			url = url.replace('{schemeRef}', selectedBuilding).trim();
			url += `?recordTypeDescription=${14}`;

			const headers = this.Utility.headersWithContentType;
			const data = await this.$http.get(url, { headers });

			this.$timeout(() => promiseHandler.resolve(data.data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getInterestTransactionDetails(accountCode, date) {
		let promise;
		let promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('InterestTransactionDetails');
			if (!!!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('InterestTransactionDetails');
			}

			const selectedBuilding = this.Utility.selectedBuildingRef;

			url += `?accountCode=${accountCode}&date=${date}`;
			url = url.replace('{schemeRef}', selectedBuilding);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.get(url, { headers });
			const { Data, WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.statements = this._mapStatements(Data);
				this.$timeout(() => promiseHandler.resolve(Data));
			} else {
				throw new Error(`Could not load Interest Transaction Details Account: ${accountCode}, Date: ${date}`);
			}
		} catch (error) {
			this.statements = [];
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async getTransactionsForYear({ schemeRef, date, accountCode }) {
		let promise;
		let promiseHandler = {};

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialApiTransactionsForYear');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiInterestTransactions');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			url = `${url}?accountCode=${accountCode}`;
			date = this.FinancialsService.getMapFirstDay(date);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, date, { headers });
			const { data } = results;

			if (`${accountCode}` in this.transactions) {
				delete this.transactions[accountCode];
			}

			this.transactions = {
				...this.transactions,
				[accountCode]: this._mapYearlyTransactions(data)
			};

			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	_mapYearlyTransactions(original) {
		let transactions = [];

		original.map((trans) => {
			transactions = [ ...transactions, ...trans.Transactions ];
		});

		return transactions;
	}

	_mapStatements(original) {
		let mapped = original.map((attachment) => {
			const { Id, Description } = attachment;

			const attachmentUrl = `${pretorOnlineUrl}${pretorOnlineUrl.endsWith('/')
				? ''
				: '/'}documents/byid/${Id}/?handleNotFound=false`;

			return {
				Id,
				Description,
				documentsUrl: this.$sce.trustAsResourceUrl(attachmentUrl)
			};
		});

		return mapped;
	}
}

export default /* @ngInject */ (
	$sce,
	$http,
	Utility,
	$timeout,
	$rootScope,
	ContentService,
	FinancialsService,
	LocalStorageService,
	InternalLinksService
) =>
	new CashflowStatementDataService(
		$sce,
		$http,
		Utility,
		$timeout,
		$rootScope,
		ContentService,
		FinancialsService,
		LocalStorageService,
		InternalLinksService
	);
